import axios from 'axios';

import { User } from '../models/User';

import { healioSpaceAPIURL } from './baseHealioSpaceAPI';
import { installSessionInterceptors } from './sessionInterceptors';
export const usersApi = axios.create({
  baseURL: `${healioSpaceAPIURL}/user`,
  withCredentials: true,
});

installSessionInterceptors(usersApi);

export const fetchAllUsers = async () => {
  try {
    const response = await usersApi.get('');

    return response.data as User[];
  } catch (error) {
    console.error('Error fetching data:', error);
  }
};

export const getUserByEmail = async (email: string) => {
  try {
    const response = await usersApi.get(`/${email}`);

    return response.data as User;
  } catch (error) {
    console.error('Error fetching user:', error);
  }
};

export const addUser = async (user: User) => {
  try {
    const response = await usersApi.post('', user);

    return response.data as User;
  } catch (error) {
    console.error('Error adding user:', error);
  }
};

export const addNewUser = async () => {
  try {
    const response = await usersApi.post('');

    return response.data as User;
  } catch (error) {
    console.error('Error adding new user:', error);
  }
};

export const inviteUser = async (email: string, role: string) => {
  try {
    const response = await usersApi.post('/InviteUser', { email, role });

    return response.data;
  } catch (error) {
    console.error('Error inviting user:', error);

    // Extract error message from API response if available
    if (axios.isAxiosError(error) && error.response) {
      const errorData = error.response.data;
      const status = error.response.status;

      // Handle specific HTTP status codes
      if (status === 403) {
        // Check for license-specific error messages
        const message =
          typeof errorData === 'object' && errorData.message
            ? errorData.message
            : typeof errorData === 'string'
              ? errorData
              : '';

        // HA-453: a role at its plan limit. The server names the role and the count, e.g.
        // "Practitioner limit exceeded (1/1). Additional practitioners are available on Enterprise tier."
        if (typeof errorData === 'object' && errorData.code === 'LimitExceeded' && message) {
          throw new Error(message);
        }

        // Specific message for missing license
        if (
          message.toLowerCase().includes('license') ||
          message.toLowerCase().includes('no active license')
        ) {
          throw new Error(
            'Unable to send invitation. Your practice does not have an active license. Please contact support at support@healiospace.com.'
          );
        }
        throw new Error(
          'Your practice license does not allow this action. Please contact support at support@healiospace.com.'
        );
      }

      if (status === 429) {
        throw new Error('Too many requests. Please wait a moment and try again.');
      }

      if (status === 409) {
        throw new Error(
          'This user has already been invited or is already a member of your practice.'
        );
      }

      // Handle structured error responses
      if (typeof errorData === 'object' && errorData.message) {
        throw new Error(errorData.message);
      }
      if (typeof errorData === 'string') {
        throw new Error(errorData);
      }
    }

    // Generic fallback error
    throw new Error('Unable to send invitation. Please try again later.');
  }
};

// Invitation management types
export interface Invitation {
  email: string;
  role?: string;
  sentDate: string;
  expirationDate: string;
  status: string;
  username?: string;
  isExpired: boolean;
}

/**
 * Get all pending invitations for the practice
 */
export const getPendingInvitations = async (): Promise<Invitation[]> => {
  try {
    const response = await usersApi.get('/Invitations');

    return response.data as Invitation[];
  } catch (error) {
    console.error('Error fetching pending invitations:', error);
    throw error;
  }
};

/**
 * Resend an invitation to a user
 */
export const resendInvitation = async (email: string): Promise<Invitation> => {
  try {
    const response = await usersApi.post(`/Invitations/${encodeURIComponent(email)}/Resend`);

    return response.data as Invitation;
  } catch (error) {
    console.error('Error resending invitation:', error);
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      throw new Error('Invitation not found or user has already accepted.');
    }
    if (axios.isAxiosError(error) && error.response?.status === 403) {
      throw new Error('Your practice license does not allow this action.');
    }
    throw error;
  }
};

/**
 * Revoke a pending invitation
 */
export const revokeInvitation = async (email: string): Promise<void> => {
  try {
    await usersApi.delete(`/Invitations/${encodeURIComponent(email)}`);
  } catch (error) {
    console.error('Error revoking invitation:', error);
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      throw new Error('Invitation not found or user has already accepted.');
    }
    throw error;
  }
};
